import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./PoliticalAdvocacy.css";
import { 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Activity,
  Mail,
  MessageSquare,
  Phone,
  CheckCircle2,
  FileText,
  Megaphone,
  Vote,
  Heart,
  PenTool,
  BarChart3,
  Target,
  TrendingUp,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";


// ============================================
// POLITICAL ADVOCACY VISUALIZATIONS
// ============================================

// Campaign Performance Metrics Visualization
const AdvocacyMetricsVisualization = () => {
  const metrics = [
    { label: 'Advocates Recruited', value: '11.12M', trend: '+24%' },
    { label: 'Messages to Lawmakers', value: '124K+', trend: '+18%' },
    { label: 'Successful Campaigns', value: '578', trend: '+32%' },
    { label: 'Donor Conversions', value: '50K+', trend: '+15%' },
  ];

  return (
    <div className="pa-viz-container metrics-viz">
      <div className="pa-viz-header">
        <span className="pa-viz-label">CAMPAIGN PERFORMANCE (2025)</span>
        <span className="pa-viz-badge green">LIVE</span>
      </div>
      
      <div className="metrics-grid">
        {metrics.map((m, i) => (
          <div key={i} className="metric-card">
            <span className="metric-value">{m.value}</span>
            <span className="metric-label">{m.label}</span>
            <span className="metric-trend positive">{m.trend}</span>
          </div>
        ))}
      </div>
      
      <div className="metrics-footer">
        <div className="metric-indicator">
          <Activity size={14} />
          <span>Real-time tracking across all campaigns</span>
        </div>
      </div>
    </div>
  );
};

// Advocacy Channels Visualization
const AdvocacyChannelsVisualization = () => {
  const channels = [
    { name: 'Email to Lawmakers', reach: '45.2K', status: 'active', icon: Mail },
    { name: 'Live Calls', reach: '12.8K', status: 'active', icon: Phone },
    { name: 'SMS Campaigns', reach: '28.4K', status: 'active', icon: MessageSquare },
    { name: 'Digital Petitions', reach: '67.1K', status: 'active', icon: PenTool },
  ];

  return (
    <div className="pa-viz-container channels-viz">
      <div className="pa-viz-header">
        <span className="pa-viz-label">ACTIVE CHANNELS</span>
        <span className="pa-viz-badge">MULTI-TOUCH</span>
      </div>
      
      <div className="channels-list">
        {channels.map((ch, i) => (
          <div key={i} className="channel-row">
            <div className="channel-icon">
              <ch.icon size={16} />
            </div>
            <div className="channel-info">
              <span className="channel-name">{ch.name}</span>
              <span className="channel-reach">{ch.reach} engagements</span>
            </div>
            <div className={`channel-status ${ch.status}`}>
              <span className="status-dot"></span>
              {ch.status}
            </div>
          </div>
        ))}
      </div>
      
      <div className="channels-footer">
        <div className="ch-metric">
          <Target size={14} />
          <span>Precision constituent targeting</span>
        </div>
      </div>
    </div>
  );
};

// Coalition Building Visualization
const CoalitionVisualization = () => {
  const segments = [
    { name: 'High-Propensity Advocates', count: '2.4M', score: 94 },
    { name: 'Issue-Aligned Voters', count: '8.7M', score: 78 },
    { name: 'Potential Donors', count: '1.2M', score: 85 },
    { name: 'Geographic Targets', count: '4.6M', score: 72 },
  ];

  return (
    <div className="pa-viz-container coalition-viz">
      <div className="pa-viz-header">
        <span className="pa-viz-label">COALITION SEGMENTS</span>
        <span className="pa-viz-id">AI-OPTIMIZED</span>
      </div>
      
      <div className="coalition-segments">
        {segments.map((seg, i) => (
          <div key={i} className="coalition-row">
            <div className="coalition-info">
              <span className="coalition-name">{seg.name}</span>
              <span className="coalition-count">{seg.count} profiles</span>
            </div>
            <div className="coalition-score">
              <div className="score-bar">
                <div 
                  className="score-fill" 
                  style={{ width: `${seg.score}%` }}
                />
              </div>
              <span className="score-value">{seg.score}</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="coalition-footer">
        <div className="coal-stat">
          <span className="stat-num">16.9M</span>
          <span className="stat-txt">Total Addressable</span>
        </div>
        <div className="coal-stat">
          <span className="stat-num">99.2%</span>
          <span className="stat-txt">Match Rate</span>
        </div>
      </div>
    </div>
  );
};

// Campaign Results Visualization
const ResultsVisualization = () => {
  const results = [
    { metric: 'Messages Delivered', wimg: '669,294', industry: '~50,000', multiplier: '13x' },
    { metric: 'Advocates Activated', wimg: '222,451', industry: '~15,000', multiplier: '15x' },
    { metric: 'Response Rate', wimg: '34.2%', industry: '~8%', multiplier: '4x' },
  ];

  return (
    <div className="pa-viz-container results-viz">
      <div className="pa-viz-header">
        <span className="pa-viz-label">PERFORMANCE BENCHMARK</span>
        <span className="pa-viz-badge green">VERIFIED</span>
      </div>
      
      <div className="results-table">
        <div className="results-header">
          <span>Metric</span>
          <span>IntentLogic</span>
          <span>Industry Avg</span>
          <span>Lift</span>
        </div>
        {results.map((r, i) => (
          <div key={i} className="results-row">
            <span className="result-metric">{r.metric}</span>
            <span className="result-wimg">{r.wimg}</span>
            <span className="result-industry">{r.industry}</span>
            <span className="result-multiplier">{r.multiplier}</span>
          </div>
        ))}
      </div>
      
      <div className="results-footer">
        <BarChart3 size={14} />
        <span>Proprietary targeting outperforms traditional methods by 10x+</span>
      </div>
    </div>
  );
};

// Hero Section
const PAHero = () => {
  return (
    <section className="pa-hero" data-testid="pa-hero">
      <div className="pa-hero-content">
        <div className="pa-hero-text">
          <span className="pa-label">POLITICAL & ADVOCACY</span>
          <h1>Data-Driven Advocacy & Political Solutions</h1>
          <p>
            Mobilize grassroots support at scale with precision targeting. Our 
            AI-powered platform identifies and activates the constituents most 
            likely to take action—delivering measurable impact for campaigns 
            and causes.
          </p>
          <Button 
            className="btn-primary-zeta"
            onClick={() => document.getElementById('pa-cta')?.scrollIntoView({ behavior: 'smooth' })}
          >
            See How It Works
            <ArrowRight size={18} />
          </Button>
        </div>
        <div className="pa-hero-visual">
          <AdvocacyMetricsVisualization />
        </div>
      </div>
    </section>
  );
};

// Stats Section
const PAStats = () => {
  const stats = [
    { value: '11.12M', label: 'Advocates Recruited' },
    { value: '124K+', label: 'Calls to Lawmakers' },
    { value: '578', label: 'Successful Campaigns' },
    { value: '50K+', label: 'Online Donations' },
  ];

  return (
    <section className="pa-stats" data-testid="pa-stats">
      <div className="pa-stats-grid">
        {stats.map((stat, i) => (
          <div key={i} className="pa-stat-card">
            <span className="pa-stat-value">{stat.value}</span>
            <span className="pa-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

// Solutions Section
const PASolutions = () => {
  const solutions = [
    { 
      icon: Users, 
      title: 'Advocate Recruitment', 
      desc: 'Identify and activate constituents through precision-targeted outreach campaigns.' 
    },
    { 
      icon: Heart, 
      title: 'Donor Recruitment', 
      desc: 'Build your fundraising base with AI-identified high-propensity donors.' 
    },
    { 
      icon: Megaphone, 
      title: 'Coalition Building', 
      desc: 'Expand your member base to shape policy and influence public opinion.' 
    },
    { 
      icon: Vote, 
      title: 'GOTV Campaigns', 
      desc: 'Mobilize supporters and drive them to the polls when it matters most.' 
    },
    { 
      icon: TrendingUp, 
      title: 'Fundraising', 
      desc: 'Generate the financial support your campaign or cause needs to win.' 
    },
    { 
      icon: BarChart3, 
      title: 'Voter Surveys', 
      desc: 'Gather real voter insights to shape smarter messaging and strategy.' 
    },
  ];

  return (
    <section className="pa-solutions-section" data-testid="pa-solutions">
      <div className="pa-solutions-content">
        <div className="pa-section-header">
          <span className="pa-section-label">OUR SOLUTIONS</span>
          <h2>Full-Spectrum Advocacy Capabilities</h2>
          <p>
            From grassroots mobilization to high-dollar fundraising, our data-driven 
            approach delivers measurable results across all advocacy objectives.
          </p>
        </div>
        
        <div className="pa-solutions-grid">
          {solutions.map((sol, i) => (
            <div key={i} className="pa-solution-card">
              <div className="pa-solution-icon">
                <sol.icon size={24} />
              </div>
              <h3>{sol.title}</h3>
              <p>{sol.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Calls to Action Section
const PACTAs = () => {
  const actions = [
    {
      title: 'Write Your Lawmaker',
      desc: 'Deploy digital campaigns using proprietary government-messaging technology to deliver targeted messages directly to any Senator, Representative, Council Member, or agency.'
    },
    {
      title: 'Call Your Lawmaker',
      desc: 'Amplify grassroots impact with our dedicated voice team. Live callers educate your audience and connect constituents with lawmakers for real-time conversations.'
    },
    {
      title: 'Comment on Regulation',
      desc: 'Execute digital campaigns to submit and publish comments on Federal and State regulatory dockets, ensuring your voice is heard where it counts.'
    },
    {
      title: 'Sign a Petition',
      desc: 'Mobilize support from ballot initiatives to referendum petitions. Capture email and mobile opt-ins while gathering advocate signatures.'
    },
  ];

  return (
    <section className="pa-ctas-section">
      <div className="pa-section-header">
        <span className="pa-section-label">CALLS TO ACTION</span>
        <h2>Activate Your Supporters</h2>
      </div>
      
      <div className="pa-ctas-grid">
        {actions.map((action, i) => (
          <div key={i} className="pa-cta-card">
            <h3>{action.title}</h3>
            <p>{action.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// Feature Section Component
const PAFeatureSection = ({ 
  eyebrow,
  title, 
  description, 
  bullets, 
  visualization: Visualization,
  reversed = false
}) => {
  return (
    <section className={`pa-feature-section ${reversed ? 'reversed' : ''}`}>
      <div className="pa-feature-wrapper">
        <div className="pa-feature-visual">
          <Visualization />
        </div>
        <div className="pa-feature-text">
          <span className="pa-eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p className="pa-feature-desc">{description}</p>
          <div className="pa-bullet-list">
            {bullets.map((bullet, index) => (
              <div key={index} className="pa-bullet-item">
                <CheckCircle2 size={18} className="bullet-icon" />
                <p>{bullet}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Case Studies Section
const PACaseStudies = () => {
  const cases = [
    {
      title: 'Multi-State Grassroots Campaign',
      goal: 'Mobilize grassroots advocates across multiple states to stop industry-backed legislation.',
      result: '37,000+ emails to legislators, 10,000+ phone calls, successful outcomes in 10 of 12 targeted states.',
    },
    {
      title: 'Federal Advocacy Campaign',
      goal: 'Drive large-scale grassroots engagement to influence federal policymakers.',
      result: '222,451 advocates activated, 669,294 messages delivered—outperforming traditional methods by 100x.',
    },
  ];

  return (
    <section className="pa-cases-section" data-testid="pa-cases">
      <div className="pa-section-header">
        <span className="pa-section-label">CASE STUDIES</span>
        <h2>Documented Campaign Results</h2>
      </div>
      
      <div className="pa-cases-grid">
        {cases.map((c, i) => (
          <div key={i} className="pa-case-card">
            <h3>{c.title}</h3>
            <div className="case-goal">
              <strong>Objective:</strong> {c.goal}
            </div>
            <div className="case-result">
              <strong>Outcome:</strong> {c.result}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// Differentiators Section
const PADifferentiators = () => {
  const diffs = [
    { title: 'Fixed CPA', desc: 'Fixed Cost Per Acquisition with no conversion worries.' },
    { title: 'No Ad Blocking', desc: 'Political ads reach audiences without platform restrictions.' },
    { title: 'Same-Day Setup', desc: 'No waiting for third-party content approvals.' },
    { title: 'Expert Team', desc: 'Decades of political & advocacy campaign experience.' },
  ];

  return (
    <section className="pa-diff-section">
      <div className="pa-section-header">
        <span className="pa-section-label">WHY INTENTLOGIC</span>
        <h2>What Makes Us Different</h2>
      </div>
      
      <div className="pa-diff-grid">
        {diffs.map((d, i) => (
          <div key={i} className="pa-diff-card">
            <h3>{d.title}</h3>
            <p>{d.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// FAQ Section
const PAFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "How does IntentLogic identify likely advocates and donors?",
      answer: "We leverage our proprietary database of 245M+ consumer profiles enriched with behavioral, demographic, and issue-alignment signals. Our AI scores individuals based on their propensity to take specific advocacy actions—whether signing petitions, calling lawmakers, or making donations."
    },
    {
      question: "Can you target specific congressional districts or states?",
      answer: "Yes. Our geo-targeting capabilities enable precision outreach at the state, congressional district, city, and even zip code level. We can activate constituents for specific legislators or target swing districts for maximum impact."
    },
    {
      question: "How do you deliver messages to lawmakers?",
      answer: "We use proprietary government-messaging technology that delivers constituent communications directly to congressional offices, state legislators, local council members, the White House, and federal/state agencies. All messages include verified constituent information."
    },
    {
      question: "What's your typical performance vs. traditional advocacy methods?",
      answer: "Our campaigns consistently outperform traditional social media and digital advertising by 10-100x in advocate activation and message volume. Precision targeting reaches highly engaged constituents at scale, delivering dramatically stronger action rates."
    },
    {
      question: "How quickly can an advocacy campaign launch?",
      answer: "Most campaigns can be live within 24-48 hours. Because we own our data and activation infrastructure, there's no waiting for third-party approvals. We handle strategy, targeting, creative development, and deployment end-to-end."
    }
  ];

  return (
    <section className="pa-faq-section" data-testid="pa-faq">
      <h2>Frequently Asked Questions</h2>
      <div className="pa-faq-list">
        {faqs.map((faq, index) => (
          <div 
            key={index} 
            className={`pa-faq-item ${openIndex === index ? 'open' : ''}`}
          >
            <button 
              className="pa-faq-question"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.question}</span>
              {openIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openIndex === index && (
              <div className="pa-faq-answer">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// CTA Section
const PACTA = () => (
  <section className="pa-cta-section" id="pa-cta" data-testid="pa-cta">
    <div className="pa-cta-content">
      <h2>Ready to Mobilize Your Supporters?</h2>
      <p>
        Schedule a consultation to learn how our proprietary data and 
        AI-powered platform can deliver measurable advocacy results.
      </p>
      <Button 
        className="btn-primary-zeta"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        Schedule Consultation
        <ArrowRight size={18} />
      </Button>
    </div>
  </section>
);

// Main Political Advocacy Page Component
function PoliticalAdvocacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="pa-page" data-testid="political-advocacy-page">
        <SEO 
          title="Political Advocacy - Data-Driven Campaign Solutions"
          description="Power your political and advocacy campaigns with AI-driven targeting, constituent identification, and multi-channel activation. Reach the right voters with the right message."
          keywords="political advertising, advocacy campaigns, voter targeting, constituent engagement, political marketing, grassroots mobilization, campaign analytics, public affairs"
          canonical="/political-advocacy"
        />
        <PAHero />
        <PAStats />
        <PASolutions />
        
        <PAFeatureSection
          eyebrow="Precision Targeting"
          title="AI-Powered Constituent Identification"
          description="Our IRIS platform analyzes millions of data points to identify the constituents most likely to take action for your cause."
          bullets={[
            "Propensity scoring identifies high-intent advocates and donors.",
            "Issue-alignment modeling targets supporters of specific policies.",
            "Geographic precision reaches constituents in targeted districts."
          ]}
          visualization={CoalitionVisualization}
        />
        
        <PAFeatureSection
          eyebrow="Multi-Channel Activation"
          title="Coordinated Advocacy Campaigns"
          description="Orchestrate grassroots action across email, voice, SMS, and digital channels—with unified tracking and optimization."
          bullets={[
            "Automated sequencing delivers sustained pressure campaigns.",
            "Live voice callers connect constituents directly with lawmakers.",
            "Real-time dashboards track engagement and legislative impact."
          ]}
          visualization={AdvocacyChannelsVisualization}
          reversed
        />
        
        <PACTAs />
        
        <PAFeatureSection
          eyebrow="Proven Results"
          title="Performance That Outpaces Industry Standards"
          description="Our precision targeting consistently delivers 10-100x better results than traditional advocacy methods."
          bullets={[
            "Proprietary data delivers dramatically higher action rates.",
            "AI optimization maximizes budget efficiency and impact.",
            "Documented success across hundreds of campaigns."
          ]}
          visualization={ResultsVisualization}
        />
        
        <PADifferentiators />
        <PACaseStudies />
        <PAFAQ />
        <PACTA />
      </div>
    </Layout>
  );
}

export default PoliticalAdvocacyPage;
